import { Trans, useLingui } from '@lingui/react/macro'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import { useEffect, useRef } from 'react'

import { H2 } from '../../_components/H2'
import { PrimaryButton } from '../../_components/PrimaryButton'

import LazyImage from '@/components/LazyImage'
import { useDotButton } from '@/hooks/carousel'
import { useLocale } from '@/hooks/routes'


import { cls } from 'tagged-classnames-free';
const FeatureIntroduction: React.FC = () => {
  const { t } = useLingui()
  const tagContainerRef = useRef<HTMLDivElement>(null)
  const locale = useLocale()
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      direction: locale === 'ar' ? 'rtl' : 'ltr',
    },
    [
      Autoplay({
        delay: 10000,
        stopOnInteraction: false,
        stopOnMouseEnter: true,
        stopOnFocusIn: true,
      }),
    ],
  )
  const { selectedIndex, onDotButtonClick } = useDotButton(emblaApi)

  useEffect(() => {
    if (tagContainerRef.current && selectedIndex >= 0) {
      const container = tagContainerRef.current
      const activeTag = container.children[selectedIndex] as HTMLElement

      if (activeTag) {
        const containerWidth = container.offsetWidth
        const tagLeft = activeTag.offsetLeft
        const tagWidth = activeTag.offsetWidth
        const scrollLeft = tagLeft - containerWidth / 2 + tagWidth / 2

        container.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth',
        })
      }
    }
  }, [selectedIndex])

  const TagList = [
    {
      tag: <Trans>AI Music Generator</Trans>,
      title: t`AI Music Generator`,
      desc: (
        <Trans>
          Transform your prompts into original AI music. Customize lyrics,
          styles and music weight to produce high-quality soundtracks.
        </Trans>
      ),
      image: '/images/index/ai-music-generator.png',
      button: {
        text: <Trans>Try AI Music Generator</Trans>,
        href: '/ai-music-generator',
      },
    },
    {
      tag: <Trans>AI Rap Generator</Trans>,
      title: t`AI Rap Generator`,
      desc: (
        <Trans>
          Instantly generate professional-quality, custom AI rap songs complete
          with beats and lyrics by simply describing your ideas.
        </Trans>
      ),
      image: '/images/index/ai-rap-generator.png',
      button: {
        text: <Trans>Try AI Rap Generator</Trans>,
        href: '/ai-rap-generator',
      },
    },
    {
      tag: <Trans>AI Cover Generator</Trans>,
      title: t`AI Cover Generator`,
      desc: (
        <Trans>
          Upload your reference audio, describe your desired sound and customize
          your track to create AI cover songs effortlessly.
        </Trans>
      ),
      image: '/images/index/ai-cover-generator.png',
      button: {
        text: <Trans>Try AI Cover Generator</Trans>,
        href: '/ai-cover-generator',
      },
    },
    {
      tag: <Trans>Text to Speech</Trans>,
      title: t`Text to Speech`,
      desc: (
        <Trans>
          Input your written text and select your preferred voice to generate
          clear, natural-sounding spoken audio across more than 50 languages.
        </Trans>
      ),
      image: '/images/index/text-to-speech.png',
      button: {
        text: <Trans>Try Text to Speech</Trans>,
        href: '/text-to-speech',
      },
    },
  ]

  return (
    <div
      className={cls`relative z-[1] mx-auto max-w-[1160px] overflow-visible px-4`}
    >
      <H2 className='pt-3 font-normal md:pt-6 lg:font-bold'>
        <Trans>AI Music Tools All in One Place</Trans>
      </H2>
      <p className='text-f-other-10 pt-2 text-center text-xs md:pt-3 md:text-xl'>
        <Trans>
          Elevate your workflow with the latest additions to Tad AI. We have
          expanded our features to give you even more versatility.
        </Trans>
      </p>
      <div className='mt-4 w-full overflow-visible lg:mt-10'>
        <div className='hidden items-center justify-center lg:flex lg:justify-center'>
          <div
            ref={tagContainerRef}
            className='relative flex items-center gap-0 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
          >
            {TagList.map((item, index) => (
              <div
                key={item.image}
                className={cls`mb-4 shrink-0 cursor-pointer whitespace-nowrap px-5 py-2 text-base font-normal transition-colors ${
                  selectedIndex === index
                    ? 'text-f-primary-hover border-f-primary-hover border-b-2 !pb-1.5 font-semibold'
                    : 'text-f-text-other-10'
                }`}
                onClick={() => onDotButtonClick(index)}
              >
                {item.tag}
              </div>
            ))}
          </div>
        </div>

        <div className='relative isolate mt-2 w-full overflow-visible lg:mt-0'>
          <div className='pointer-events-none absolute inset-0 z-0 hidden translate-y-[12px] scale-x-[1.2] scale-y-[1.6] lg:block'>
            <LazyImage
              fill
              src='/images/index/feature-introduction-bg.png'
              alt='Feature Introduction Background'
            />
          </div>
          <div className='relative z-[1] w-full lg:rounded-3xl lg:bg-[#2E243D] lg:p-[6px]'>
            <div
              ref={emblaRef}
              className='w-full overflow-hidden bg-[linear-gradient(180deg,#0E051900_0%,#1C0833_100%)] lg:rounded-[18px] lg:bg-[#000000A3] lg:px-16 lg:py-12'
            >
              <div className='flex w-full gap-7 lg:gap-16'>
                {TagList.map((item) => (
                  <div
                    key={item.image}
                    className='flex max-w-max flex-[0_0_100%] flex-col-reverse items-center gap-4 overflow-visible lg:gap-8 xl:flex-row xl:items-stretch'
                  >
                    <div className='flex flex-col items-start justify-between px-3 pb-4 lg:px-0 lg:pb-0 xl:self-stretch'>
                      <div>
                        <p className='text-f-other-10 text-lg lg:text-4xl'>
                          {item.title}
                        </p>
                        <p className='text-f-other-9 mt-1.5 text-xs lg:mt-4 lg:text-lg'>
                          {item.desc}
                        </p>
                      </div>
                      <PrimaryButton
                        href={item.button.href}
                        className='mt-4 w-full !py-3 !text-base font-semibold lg:mt-0 lg:w-fit'
                      >
                        {item.button.text}
                      </PrimaryButton>
                    </div>
                    {item.image ? (
                      <LazyImage
                        width={480}
                        height={320}
                        src={item.image}
                        alt={item.title}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className='mx-auto mt-3 flex w-fit items-center gap-1 lg:hidden'>
          {Array.from({ length: TagList.length }).map((_, index) => (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={`size-1.5 cursor-pointer rounded-full ${index === selectedIndex ? 'bg-f-primary-hover' : 'bg-white'}`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
export default FeatureIntroduction
